'use client'

import * as React from 'react'
import { useSearchParams } from 'next/navigation'

import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { Button, IconButton, InputAdornment, styled, TextField } from '@mui/material'
import { useRouter as useRouterLoader } from 'nextjs-toploader/app'

import { getSearchPageUrl } from '@/helpers/getSearchPageUrl'
import { getSearchQueryText } from '@/helpers/getSearchQueryText'
import { useHeaderContext } from '@/hooks/useHeaderContext'
import { useQuickSearchReset } from '@/hooks/useQuickSearchReset'
import { useTranslations } from '@/hooks/useTranslations'
import { QuickSearchContext } from '@/providers/QuickSearchContextProvider'

import type { Theme } from '@mui/material'
import type { SystemStyleObject } from '@mui/system'

const StyledForm = styled('form')(({ theme }) => ({
  [`@media (max-width: ${theme.breakpoints.values.md}px)`]: {
    backgroundColor: theme.palette.background.component,
    padding: theme.spacing(1),
  },
  width: '100%',
  backgroundColor: 'transparent',
}))

type SearchBarVariant = 'desktop' | 'mobile'

interface SearchBarProps {
  variant: SearchBarVariant
}

export const SearchBar: React.FC<SearchBarProps> = ({ variant }) => {
  const isMobileLayout = variant === 'mobile'
  const { translate } = useTranslations()
  const { searchText, setSearchText } = React.useContext(QuickSearchContext)
  const { reset } = useQuickSearchReset()
  const { isSearchOpen, setIsSearchOpen } = useHeaderContext()

  const { push } = useRouterLoader()
  const searchParams = useSearchParams()

  const initialQuery = React.useMemo(() => {
    const q = searchParams?.get('q')

    return getSearchQueryText(q ?? undefined)
  }, [searchParams])

  const simpleSearch = React.useCallback(() => {
    setIsSearchOpen(false)
    push(getSearchPageUrl(searchText))
  }, [push, searchText, setIsSearchOpen])

  const closeSearch = () => {
    reset()
    setIsSearchOpen(false)
  }

  const startAdornment = () => {
    if (isSearchOpen && isMobileLayout) {
      return (
        <InputAdornment position="start" sx={{ marginLeft: 1 }}>
          <IconButton aria-label="search" edge="start" size="large" onClick={simpleSearch}>
            <SearchIcon fontSize="large" />
          </IconButton>
        </InputAdornment>
      )
    }

    return null
  }

  const endAdornment = () => {
    if (isSearchOpen && isMobileLayout) {
      return (
        <InputAdornment position="end">
          <IconButton aria-label="close" edge="end" size="large" onClick={closeSearch}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </InputAdornment>
      )
    }

    return (
      <InputAdornment position="end" sx={{ marginLeft: isMobileLayout ? 0 : 1 }}>
        <Button
          variant="link"
          aria-label="search"
          size={isMobileLayout ? 'large' : 'medium'}
          onClick={simpleSearch}
          data-testid={`searchBar-${variant}-submitButton`}
        >
          <SearchIcon fontSize={isMobileLayout ? 'large' : 'small'} />
          {translate('common.search')}
        </Button>
      </InputAdornment>
    )
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    simpleSearch()
  }

  React.useEffect(() => {
    if (initialQuery != null && initialQuery !== '') {
      setSearchText(initialQuery)
    }
  }, [initialQuery, setSearchText])

  return (
    <StyledForm onSubmit={handleSubmit}>
      <TextField
        fullWidth
        autoComplete="off"
        role="search"
        value={searchText}
        onFocus={() => {
          if (!isSearchOpen) {
            setIsSearchOpen(true)
          }
        }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const currentSearchText = e.currentTarget.value
          setSearchText(currentSearchText)
        }}
        placeholder={translate('form.search.placeholder')?.toString()}
        InputProps={{
          'aria-label': translate('common.search')?.toString(),
          startAdornment: startAdornment(),
          endAdornment: endAdornment(),
          inputProps: {
            tabIndex: -1,
            'data-testid': `searchBar-${variant}-input`,
          },
        }}
        sx={(theme): SystemStyleObject<Theme> =>
          isMobileLayout
            ? {
                '& .MuiInputBase-input': {
                  height: theme.spacing(4),
                },
                '& .MuiInputBase-root.MuiInputBase-formControl': {
                  borderRadius: theme.spacing(3),
                  paddingLeft: theme.spacing(1),
                  paddingRight: theme.spacing(2),
                },
              }
            : {
                '& .MuiInputBase-input': {
                  height: theme.spacing(3),
                },
                '& .MuiInputBase-root.MuiInputBase-formControl': {
                  backgroundColor: theme.palette.background.pale,
                  minWidth: 200,
                  maxWidth: 550,
                  borderRadius: theme.spacing(3),
                  border: 'none',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent',
                },
              }
        }
      />
    </StyledForm>
  )
}
